import React, { useState, useEffect, useRef } from 'react';
import { Button, Box, Typography, Paper, Tabs, Tab, Stack, Chip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import axios from 'axios';
import OrderTable from './OrderTable';
import OrderResponsesTable from './OrderResponsesTable';
import OrderStatusTable from './OrderStatusTable';

const Monitor = ({ isMonitoring, setIsMonitoring }) => {
  const [orders, setOrders] = useState([]);
  const [latestMessage, setLatestMessage] = useState('');
  const [latestShoonyaSymbol, setLatestShoonyaSymbol] = useState('');
  const [monitorStatus, setMonitorStatus] = useState({ master: '', shoonya: '', zerodha: '' });
  const [loggedInAccounts, setLoggedInAccounts] = useState({ shoonya: [], zerodha: [] });
  const [orderResponses, setOrderResponses] = useState([]);
  const [orderStatus, setOrderStatus] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const pollTimeoutRef = useRef(null);
  const lastPollTime = useRef(Date.now());

  useEffect(() => {
    const handler = (e) => {
      if (isMonitoring) { e.preventDefault(); e.returnValue = 'Monitoring is active.'; return e.returnValue; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isMonitoring]);

  const pollOrders = async () => {
    try {
      const now = Date.now();
      if (now - lastPollTime.current > 2500) console.warn(`Poll delay: ${now - lastPollTime.current}ms`);
      const r = await axios.get('/api/orders', { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } });
      const newOrders = r.data.orders;
      const newResponses = r.data.order_responses;
      if (newOrders?.length > 0) {
        setOrders(prev => {
          const updated = [...prev];
          for (const order of newOrders) {
            const idx = updated.findIndex(o => o.order_id === order.order_id);
            if (idx !== -1) updated[idx] = order;
            else updated.push(order);
          }
          return updated;
        });
        setOrderResponses(prev => [...prev, ...newResponses]);
        setLatestMessage('Orders identified');
        setLatestShoonyaSymbol(newOrders[0].shoonya_symbol);
      }
    } catch { setLatestMessage('Error fetching orders.'); }
    finally {
      lastPollTime.current = Date.now();
      if (isMonitoring) pollTimeoutRef.current = setTimeout(pollOrders, 2000);
    }
  };

  const startMonitor = async () => {
    try {
      const r = await axios.post('/api/start_monitor');
      setIsMonitoring(true);
      setMonitorStatus({ master: r.data.master_account || 'Not logged in', shoonya: 'Active', zerodha: 'Active' });
      setLoggedInAccounts({ shoonya: r.data.shoonya_accounts || [], zerodha: r.data.zerodha_accounts || [] });
      // Token is now set — load today's existing orders so past orders are visible
      await Promise.all([fetchOrdersToday(), fetchOrderResponsesToday(), fetchOrderStatusToday()]);
    } catch {
      setMonitorStatus({ master: 'Login failed', shoonya: 'Error', zerodha: 'Error' });
    }
  };

  const stopMonitor = async () => {
    try {
      await axios.post('/api/stop_monitor');
      setIsMonitoring(false);
      setMonitorStatus({ master: 'Stopped', shoonya: 'Stopped', zerodha: 'Stopped' });
      setLoggedInAccounts({ shoonya: [], zerodha: [] });
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    } catch { setMonitorStatus(p => ({ ...p, shoonya: 'Error stopping', zerodha: 'Error stopping' })); }
  };

  const fetchOrdersToday = async () => {
    try { const r = await axios.get('/api/orders_today'); setOrders(r.data); } catch {}
  };
  const fetchOrderResponsesToday = async () => {
    try { const r = await axios.get('/api/order_responses_today'); setOrderResponses(r.data); } catch {}
  };
  const fetchOrderStatusToday = async () => {
    try { const r = await axios.get('/api/order_status'); setOrderStatus(r.data); } catch {}
  };

  useEffect(() => {
    if (isMonitoring) {
      const iv = setInterval(fetchOrderStatusToday, 5000);
      return () => clearInterval(iv);
    }
  }, [isMonitoring]);

  useEffect(() => { fetchOrdersToday(); fetchOrderResponsesToday(); }, []);

  useEffect(() => {
    if (isMonitoring) pollOrders();
    return () => { if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current); };
  }, [isMonitoring]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 2, gap: 2, bgcolor: 'background.default' }}>

      {/* Top Control Bar */}
      <Paper sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={startMonitor}
            disabled={isMonitoring}
            size="small"
            sx={{ minWidth: 130 }}
          >
            Start Monitor
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<StopIcon />}
            onClick={stopMonitor}
            disabled={!isMonitoring}
            size="small"
            sx={{ minWidth: 130 }}
          >
            Stop Monitor
          </Button>
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          {isMonitoring ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: '#22c55e', animation: 'pulse 2s infinite' }} />
              <Typography variant="body2" sx={{ color: '#059669', fontWeight: 600 }}>Live</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: '#94a3b8' }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Idle</Typography>
            </Box>
          )}
          {latestMessage && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              — {latestMessage}
            </Typography>
          )}
          {latestShoonyaSymbol && (
            <Chip label={latestShoonyaSymbol} size="small" sx={{ bgcolor: '#f0f9ff', color: '#0369a1', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
          )}
        </Box>
      </Paper>

      {/* Main Area */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>

        {/* Status Panel */}
        <Paper sx={{ width: 220, borderRadius: 2, p: 0, overflow: 'hidden', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle2">Accounts</Typography>
          </Box>
          <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
            {/* Master */}
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Master</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {monitorStatus.master || 'Not connected'}
            </Typography>

            {/* Zerodha */}
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Zerodha</Typography>
            {loggedInAccounts.zerodha.length > 0 ? (
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {loggedInAccounts.zerodha.map((a, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiberManualRecordIcon sx={{ fontSize: 8, color: '#22c55e' }} />
                    <Typography variant="body2">{a}</Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No accounts</Typography>
            )}

            {/* Shoonya */}
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Shoonya</Typography>
            {loggedInAccounts.shoonya.length > 0 ? (
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {loggedInAccounts.shoonya.map((a, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiberManualRecordIcon sx={{ fontSize: 8, color: '#22c55e' }} />
                    <Typography variant="body2">{a}</Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No accounts</Typography>
            )}

            {/* Stats */}
            <Box sx={{ mt: 'auto', pt: 1, borderTop: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">Orders today</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>{orders.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption">Responses</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>{orderResponses.length}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Tables Panel */}
        <Paper sx={{ flex: 1, borderRadius: 2, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          <Tabs
            value={currentTab}
            onChange={(_, v) => setCurrentTab(v)}
            sx={{
              borderBottom: '1px solid #e2e8f0',
              minHeight: 44,
              px: 1,
              '& .MuiTabs-indicator': { height: 2 },
            }}
          >
            <Tab label={`Orders (${orders.length})`} />
            <Tab label={`Order Status (${orderStatus.length})`} />
            <Tab label={`Responses (${orderResponses.length})`} />
          </Tabs>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {currentTab === 0 && <OrderTable orders={orders} />}
            {currentTab === 1 && <OrderStatusTable orderStatus={orderStatus} />}
            {currentTab === 2 && <OrderResponsesTable orderResponses={orderResponses} />}
          </Box>
        </Paper>
      </Box>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>
    </Box>
  );
};

export default Monitor;
